import React, { FC, useEffect, useState } from 'react';
import { ContentBlocker } from '@whitesharx/starfall-frontend-ui-kit';
import { Storage, useServices } from '@whitesharx/starfall-frontend-core';
import { createPortal } from 'react-dom';
import DesktopTutorial from './DesktopTutorial';
import MobileTutorial from './MobileTutorial';
import { VIEW_EVENTS } from '~/src/services/analytics/contants';
import { useEvent } from '~/src/hooks/useEvent';
import { ONBOARDING_COMPLETED_AT_KEY } from '../Game/constants';

type Props = {
  blur: boolean;
  onLastStep: () => void;
};

const restoreStepShowedAt = () => {
  return Storage.getItem<number>(ONBOARDING_COMPLETED_AT_KEY) || 0;
};

const Tutorial: FC<Props> = ({ blur, onLastStep }) => {
  const { useResponsive, Analytics } = useServices();
  const { isDesktop } = useResponsive();

  const [step, setStep] = useState(1);
  const [stepShowedAt, setStepShowedAt] = useState(restoreStepShowedAt);
  const [tutorialStarted, setTutorialStarted] = useState(false);
  const [canCompletedStep, setCanCompleteStep] = useState(isDesktop);

  const trackTutorialEvent = useEvent(() => {
    Analytics.sendEvent(VIEW_EVENTS.tutorial, {
      step: String(step),
      time: stepShowedAt ? String(Date.now() - stepShowedAt) : '0',
    });

    setStepShowedAt(Date.now());
  });

  const completeStep = () => {
    setStep(Math.min(step + 1, 3));
  };

  const handleClick = () => {
    if (canCompletedStep) {
      completeStep();
    }
  };

  useEffect(() => {
    if (step === 3) {
      onLastStep();
    }

    if (tutorialStarted) {
      trackTutorialEvent();
    }
  }, [step, tutorialStarted]);

  useEffect(() => {
    setCanCompleteStep(isDesktop || step !== 1);
  }, [isDesktop, step]);

  useEffect(() => {
    if (blur) {
      setTimeout(() => setTutorialStarted(true), 1000); // wait until blackout appears
    }
  }, [blur]);

  return createPortal(
    <>
      <ContentBlocker blur={blur} onClick={handleClick} />

      {tutorialStarted && (
        <>
          {isDesktop ? (
            <DesktopTutorial step={step} completeStep={completeStep} />
          ) : (
            <MobileTutorial step={step} completeStep={completeStep} setCanCompleteStep={setCanCompleteStep} />
          )}
        </>
      )}
    </>,
    document.getElementById('tutorial-root') as HTMLElement
  );
};

export default Tutorial;
